import React, { useState, useEffect } from 'react';

/*
  ComingSoon — shown on the PUBLIC user side when siteMode === 'development'.
  Wallet connect / register is blocked here; the admin panel (?key=) is unaffected.

  The page content is ENTIRELY admin-driven — there is NO built-in "under
  development" text. The admin supplies:
    - heading     : big title (comingSoonHeading)
    - subheading  : supporting line / message (comingSoonSubheading)
    - launchAt    : optional ISO time → shows a live countdown when set

  If heading/subheading are empty, we fall back to the token name only (still no
  "under development" wording).
*/

const pad = (n) => String(n).padStart(2, '0');

const getRemaining = (target) => {
  if (!target) return null;
  const diff = new Date(target).getTime() - Date.now();
  if (isNaN(diff)) return null;
  if (diff <= 0) return { done: true, days: 0, hours: 0, mins: 0, secs: 0 };
  return {
    done: false,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
  };
};

const ComingSoon = ({ tokenName = 'ROBO', heading = '', subheading = '', launchAt = null }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem('robo-theme') || 'dark');
  const [remaining, setRemaining] = useState(() => getRemaining(launchAt));

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('robo-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!launchAt) { setRemaining(null); return; }
    setRemaining(getRemaining(launchAt));
    const id = setInterval(() => setRemaining(getRemaining(launchAt)), 1000);
    return () => clearInterval(id);
  }, [launchAt]);

  const toggleTheme = () => setTheme((p) => (p === 'dark' ? 'light' : 'dark'));
  const showCountdown = launchAt && remaining && !remaining.done;
  const title = heading.trim() || tokenName;

  return (
    <div className="cs-root">
      <style>{`
        .cs-root {
          min-height: 100vh; display: flex; flex-direction: column;
          background: var(--bg, #0b0b12); color: var(--text, #e8e8ef);
          position: relative; overflow: hidden;
        }
        .cs-bg-glow { position: absolute; border-radius: 50%; filter: blur(120px); opacity: .5; pointer-events: none; }
        .cs-bg-glow.g1 { width: 480px; height: 480px; background: var(--accent, #6366f1); top: -140px; left: -120px; }
        .cs-bg-glow.g2 { width: 420px; height: 420px; background: #22d3ee; bottom: -160px; right: -120px; opacity: .28; }
        .cs-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 28px; position: relative; z-index: 2; }
        .cs-logo { display: flex; align-items: center; gap: 10px; font-weight: 800; letter-spacing: .04em; }
        .cs-logo-icon {
          display: inline-flex; align-items: center; justify-content: center;
          width: 38px; height: 38px; border-radius: 11px;
          background: var(--accent, #6366f1); color: #fff; font-size: 18px;
          box-shadow: 0 8px 22px rgba(99,102,241,.45);
        }
        .cs-logo-text { font-size: 20px; }
        .cs-logo-tag { font-size: 10px; font-weight: 700; letter-spacing: .18em; opacity: .55;
          border: 1px solid var(--border, #2a2a3a); padding: 3px 7px; border-radius: 6px; margin-left: 2px; }
        .cs-theme { background: transparent; border: 1px solid var(--border, #2a2a3a); color: inherit;
          width: 40px; height: 40px; border-radius: 10px; cursor: pointer; font-size: 16px; }
        .cs-main { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
          text-align: center; padding: 24px; position: relative; z-index: 2; }
        .cs-title { font-size: clamp(34px, 7vw, 66px); line-height: 1.08; margin: 0 0 18px; font-weight: 800; max-width: 900px; }
        .cs-sub { font-size: clamp(15px, 2.4vw, 20px); opacity: .75; max-width: 640px; margin: 0 auto 34px; line-height: 1.6; white-space: pre-line; }
        .cs-timer { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; margin-bottom: 8px; }
        .cs-cell { min-width: 92px; padding: 18px 14px; border-radius: 16px;
          background: var(--bg-card, rgba(255,255,255,.04)); border: 1px solid var(--border, #2a2a3a); backdrop-filter: blur(6px); }
        .cs-cell-num { font-size: clamp(30px, 6vw, 46px); font-weight: 800; font-variant-numeric: tabular-nums; line-height: 1; color: var(--accent, #818cf8); }
        .cs-cell-lbl { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; opacity: .55; margin-top: 9px; }
        .cs-note { font-size: 13px; opacity: .55; margin-top: 18px; }
        .cs-footer { text-align: center; padding: 18px; font-size: 12.5px; opacity: .45; position: relative; z-index: 2; }
        @media (max-width: 520px) { .cs-cell { min-width: 68px; padding: 14px 8px; } }
      `}</style>

      <div className="cs-bg-glow g1" />
      <div className="cs-bg-glow g2" />

      <header className="cs-header">
        <div className="cs-logo">
          <span className="cs-logo-icon">◈</span>
          <span className="cs-logo-text">{tokenName}</span>
          <span className="cs-logo-tag">TOKEN</span>
        </div>
        <button className="cs-theme" onClick={toggleTheme} title="Toggle theme">{theme === 'dark' ? '☀' : '🌙'}</button>
      </header>

      <main className="cs-main">
        <h1 className="cs-title">{title}</h1>
        {subheading.trim() && <p className="cs-sub">{subheading}</p>}

        {showCountdown && (
          <div className="cs-timer">
            <div className="cs-cell"><div className="cs-cell-num">{pad(remaining.days)}</div><div className="cs-cell-lbl">Days</div></div>
            <div className="cs-cell"><div className="cs-cell-num">{pad(remaining.hours)}</div><div className="cs-cell-lbl">Hours</div></div>
            <div className="cs-cell"><div className="cs-cell-num">{pad(remaining.mins)}</div><div className="cs-cell-lbl">Minutes</div></div>
            <div className="cs-cell"><div className="cs-cell-num">{pad(remaining.secs)}</div><div className="cs-cell-lbl">Seconds</div></div>
          </div>
        )}

        {launchAt && remaining?.done && <p className="cs-note">Please refresh in a moment.</p>}
      </main>

      <footer className="cs-footer">{tokenName} Token © {new Date().getFullYear()}</footer>
    </div>
  );
};

export default ComingSoon;
